import { useState } from 'react';
import {
  AritoIcon,
  DeleteDialog,
  ErpDataTableV2,
  ExcelImportDialog,
  ExcelImportErrorDialog
} from '@/components/custom/erp';
import { toast, useCRUD, useExcel, useExcelProcessor, useForms } from '@/hooks';
import { ExcelConfig, ImportBody } from './utils/excel-util';
import { ColumnMapping, ValidationError } from '@/lib/excel';
import { NhanVien, NhanVienInput } from '@/types/schemas';
import { getDataTableColumns } from './cols-definition';
import { ActionBar, FormDialog } from './components';
import { NhanVienProvider } from './context';
import { QUERY_KEYS } from '@/constants';
import { useSearchTable } from './hooks';

const PAGE_SIZE = 20;

export default function DanhMucNhanVienPage() {
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorData, setErrorData] = useState<{
    errors: ValidationError[];
    columnMapping: Record<string, ColumnMapping>;
  } | null>(null);

  const {
    data: nhanViens,
    totalItems,
    currentPage,
    detail,
    fetchDetail,
    handlePageChange,
    addItem: addNhanVien,
    updateItem: updateNhanVien,
    deleteItem: deleteNhanVien,
    refreshData: refreshNhanViens,
    searchData
  } = useCRUD<NhanVien, NhanVienInput>({
    endpoint: QUERY_KEYS.NHAN_VIEN,
    pageSize: PAGE_SIZE
  });

  const { processFile } = useExcelProcessor(ExcelConfig);
  const { getUUIDByCode } = useExcel();

  const {
    showForm,
    showDelete,
    formMode,
    selectedObj,
    selectedRowIndex,
    isCopyMode,
    isImportDialogOpen,
    setIsImportDialogOpen,

    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection,
    handleOpenImportExcel
  } = useForms<NhanVien>(fetchDetail);

  const { searchState, handleSearchChange, onPageChange, hasFilterParams } = useSearchTable({
    searchData,
    isFiltered: false,
    handlePageChange: handlePageChange
  });

  const displayData = hasFilterParams ? searchState.searchResults : nhanViens || [];
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;

  const handleSubmit = async (data: NhanVienInput) => {
    try {
      if (formMode === 'add') {
        await addNhanVien(data);
      } else if (formMode === 'edit' && selectedObj) {
        await updateNhanVien(selectedObj.uuid, data);
      }
      handleCloseForm();
      clearSelection();
      await refreshNhanViens();
    } catch (error) {
      return;
    }
  };

  const handleImportBodies = async (bodies: ImportBody[]) => {
    try {
      for (const body of bodies) {
        await addNhanVien(body as NhanVienInput);
      }
      await refreshNhanViens();
      toast({
        description: 'Đã nhập danh mục nhân viên từ Excel thành công',
        variant: 'default'
      });
    } catch (error) {
      console.error('Error importing employee from Excel:', error);
      toast({
        description: 'Có lỗi xảy ra khi nhập dữ liệu từ Excel',
        variant: 'destructive'
      });
      throw error;
    }
  };

  const handleImportFromExcel = async (data: { errors?: string; file: File | null; sheetIndex?: string }) => {
    if (!data.file) {
      toast({
        description: 'Vui lòng chọn file Excel',
        variant: 'destructive'
      });
      return;
    }

    const maxErrors = parseInt(data.errors || '200', 10);
    const sheetIndex = parseInt(data.sheetIndex || '0', 10);

    const result = await processFile({
      file: data.file,
      sheetIndex,
      maxErrors,
      getUUIDByCode,
      context: {}
    });

    if (result.success && result.data) {
      await handleImportBodies(result.data);
      setIsImportDialogOpen(false);
    } else if (result.errors && result.columnMapping) {
      setErrorData({ errors: result.errors, columnMapping: result.columnMapping });
      setShowErrorDialog(true);
    }
  };

  const tables = [
    {
      name: '',
      rows: displayData,
      columns: getDataTableColumns(handleViewClick)
    }
  ];

  return (
    <NhanVienProvider>
      <div className='flex h-full min-h-[calc(100vh-120px)] w-full flex-col'>
        <div className={`${showForm ? 'hidden' : 'block'}`}>
          <ActionBar
            onAdd={handleAddClick}
            onEdit={() => selectedObj && handleEditClick()}
            onDelete={() => selectedObj && handleDeleteClick()}
            onCopy={handleCopyClick}
            isEditDisabled={!selectedObj}
            onImportFromExcel={handleOpenImportExcel}
          />

          <div data-excel-operation-overlay-root className='min-h-0 flex-1 overflow-hidden'>
            <ErpDataTableV2
              tables={tables}
              onRowClick={handleRowClick}
              selectedRowId={selectedRowIndex || undefined}
              totalItems={resolvedTotalItems}
              currentPage={resolvedCurrentPage}
              onPageChange={onPageChange}
              serverSidePagination={true}
              pageSize={PAGE_SIZE}
              onSearchChange={handleSearchChange}
            />
          </div>
        </div>

        {showForm && (
          <FormDialog
            formMode={formMode}
            onSubmit={handleSubmit}
            open={showForm}
            onClose={handleCloseForm}
            initialData={formMode === 'add' && !isCopyMode ? undefined : detail}
            onAddButtonClick={() => {
              handleCloseForm();
              clearSelection();
              setTimeout(() => {
                handleAddClick();
              }, 100);
            }}
            onEditButtonClick={() => {
              if (selectedObj) {
                handleCloseForm();
                handleEditClick();
              }
            }}
            onDeleteButtonClick={() => {
              if (selectedObj) {
                handleCloseForm();
                handleDeleteClick();
              }
            }}
            onCopyButtonClick={() => {
              if (selectedObj) {
                handleCloseForm();
                handleCopyClick();
              }
            }}
          />
        )}

        {showDelete && (
          <DeleteDialog
            open={showDelete}
            onClose={handleCloseDelete}
            selectedObj={selectedObj}
            deleteObj={deleteNhanVien}
            clearSelection={clearSelection}
          />
        )}

        {isImportDialogOpen && (
          <ExcelImportDialog
            open={isImportDialogOpen}
            onClose={() => setIsImportDialogOpen(false)}
            onSubmit={handleImportFromExcel}
            title='Lấy dữ liệu từ excel'
            titleIcon={<AritoIcon icon={555} />}
          />
        )}

        {showErrorDialog && errorData && (
          <ExcelImportErrorDialog
            open={showErrorDialog}
            onClose={() => {
              setShowErrorDialog(false);
              setErrorData(null);
            }}
            errors={errorData.errors}
            columnMapping={errorData.columnMapping}
          />
        )}
      </div>
    </NhanVienProvider>
  );
}
